/**
 * LocalStorage utility for persisting app data
 */

import { logger } from './logger';

const STORAGE_KEYS = {
  LEAD_LISTS: 'mih_lead_lists',
  CAMPAIGNS: 'mih_campaigns',
  LEADS: 'mih_leads',
} as const;

type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

/**
 * Get data from localStorage with type safety
 */
export function getStoredData<T>(key: StorageKey, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return defaultValue;
    return JSON.parse(stored) as T;
  } catch (err) {
    logger.warn(`[Storage] Failed to parse ${key}: ${err instanceof Error ? err.message : String(err)}`);
    return defaultValue;
  }
}

/**
 * Save data to localStorage
 */
export function setStoredData<T>(key: StorageKey, data: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    logger.error(`[Storage] Failed to save ${key}: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * Remove data from localStorage
 */
export function removeStoredData(key: StorageKey): void {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    logger.error(`[Storage] Failed to remove ${key}: ${err instanceof Error ? err.message : String(err)}`);
  }
}

// Export storage keys for use in hooks
export { STORAGE_KEYS };
